import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { pixelTrackCustom } from "@/lib/pixel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Rocket, FileText, ClipboardCheck, Boxes, ShieldCheck, Check, X, ArrowRight, Smartphone, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const NIVELES = [
  {
    n: 1, key: "1", icon: FileText, titulo: "Documenta",
    desc: "Sube tus 5 documentos clave (Plan APPCC, trazabilidad, limpieza, política de calidad, retirada) y deja que la IA los clasifique.",
    cta: { label: "Subir con IA", to: "/subir-ia" },
  },
  {
    n: 2, key: "2", icon: ClipboardCheck, titulo: "Registra",
    desc: "Crea tu primer registro de control (pH, temperaturas, higiene…) desde el día a día.",
    cta: { label: "Ir a Calidad", to: "/calidad" },
  },
  {
    n: 3, key: "3", icon: Boxes, titulo: "Traza",
    desc: "Da de alta tu primer proveedor, materia prima y producto, y registra una recepción de ejemplo.",
    cta: { label: "Recepciones y Lotes", to: "/lotes" }, cta2: { label: "Maestros", to: "/maestros" },
  },
  {
    n: 4, key: "4", icon: ShieldCheck, titulo: "Verifica",
    desc: "Genera tu primer informe de pre-auditoría y comprueba tu readiness real.",
    cta: { label: "Ir a Informes", to: "/informes" },
  },
];

export default function OnboardingWizard() {
  const { tenant, user, reload } = useAuth();
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [expandido, setExpandido] = useState(true);

  const editable = ["admin", "calidad", "operario"].includes(user?.rol);
  const visible = tenant?.plan === "trial" && !tenant?.onboarding_completado && editable;
  const collapseKey = tenant?.id ? `onboarding_collapsed_${tenant.id}` : null;

  const cargar = useCallback(() => {
    api.get("/cuenta/resumen").then((r) => setResumen(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!visible) return;
    try { if (collapseKey && localStorage.getItem(collapseKey) === "1") setExpandido(false); } catch { /* noop */ }
    cargar();
  }, [visible, collapseKey, cargar]);

  const uso = resumen?.niveles_uso || {};
  const docs = resumen?.documentos || 0;
  const hechos = NIVELES.filter((l) => uso[l.key]).length;
  const todo = hechos === NIVELES.length;

  // Evento de medición: se muestra el onboarding (una vez por carga cuando ya hay datos).
  useEffect(() => {
    if (visible && resumen) {
      pixelTrackCustom("OnboardingVisto", { niveles_hechos: hechos, formato: expandido ? "completo" : "mini" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, !!resumen]);

  if (!visible) return null;

  const colapsar = () => {
    setExpandido(false);
    try { if (collapseKey) localStorage.setItem(collapseKey, "1"); } catch { /* noop */ }
    pixelTrackCustom("OnboardingColapsado", { niveles_hechos: hechos });
  };
  const reabrir = () => {
    setExpandido(true);
    try { if (collapseKey) localStorage.removeItem(collapseKey); } catch { /* noop */ }
    pixelTrackCustom("OnboardingReabierto", { niveles_hechos: hechos });
  };
  const irA = (nivel, to) => { pixelTrackCustom("OnboardingNivelCTA", { nivel }); navigate(to); };

  const completar = async () => {
    try {
      await api.patch("/cuenta/onboarding", { completado: true });
      pixelTrackCustom("OnboardingCompletado", { niveles_hechos: hechos });
      toast.success("¡Primeros pasos completados! 🏆");
      await reload();
    } catch { toast.error("No se pudo completar"); }
  };

  // Mini-widget persistente: siempre visible en trial aunque el usuario "salte".
  if (!expandido) {
    return (
      <Card className="p-3 sm:p-4 border-[#E2E8F0] mb-6 flex items-center gap-3" data-testid="onboarding-mini">
        <Rocket size={18} className="text-[#FF5625] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-sm font-semibold text-[#0E2A47] truncate">Primeros pasos · {hechos}/{NIVELES.length} niveles</span>
            <span className="text-xs text-[#0E2A47]/50 shrink-0" data-testid="onboarding-mini-texto">{Math.round((hechos / NIVELES.length) * 100)}%</span>
          </div>
          <Progress value={(hechos / NIVELES.length) * 100} className="h-1.5" data-testid="onboarding-mini-progreso" />
        </div>
        <Button size="sm" variant="outline" onClick={reabrir} data-testid="onboarding-reabrir" className="h-8 text-xs shrink-0">
          Continuar<ArrowRight size={14} className="ml-1" />
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-5 sm:p-6 border-[#E2E8F0] mb-6 border-l-4 border-l-[#FF5625]" data-testid="onboarding-wizard">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Rocket size={20} className="text-[#FF5625]" />
          <span className="font-black tracking-tight text-[#0E2A47]">Primeros pasos · tu camino en 4 niveles</span>
        </div>
        <button onClick={colapsar} data-testid="onboarding-saltar" className="text-xs text-[#0E2A47]/50 hover:text-[#0E2A47] flex items-center gap-1 shrink-0"><ChevronDown size={14} />Minimizar</button>
      </div>
      <p className="text-sm text-[#0E2A47]/60 mb-4">Ruta recomendada, no obligatoria. Cada nivel se marca solo cuando haces la acción real.</p>

      <div className="flex items-center gap-3 mb-5">
        <Progress value={(hechos / NIVELES.length) * 100} className="h-2 flex-1" data-testid="onboarding-progreso" />
        <span className="text-xs font-semibold text-[#0E2A47]/70 shrink-0" data-testid="onboarding-progreso-texto">{hechos}/{NIVELES.length} niveles</span>
      </div>

      <div className="space-y-2.5">
        {NIVELES.map((l) => {
          const done = !!uso[l.key];
          const Icon = l.icon;
          return (
            <div key={l.n} data-testid={`onboarding-nivel-${l.n}`} data-done={done}
              className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${done ? "border-[#10B981]/40 bg-[#F0FDF4]" : "border-[#E2E8F0] bg-white"}`}>
              <div className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${done ? "bg-[#10B981] text-white" : "bg-[#F7F8FA] text-[#0E2A47]/60"}`}>
                {done ? <Check size={18} data-testid={`onboarding-nivel-${l.n}-check`} /> : <Icon size={16} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF5625]">Nivel {l.n}</span>
                  <span className="font-semibold text-[#0E2A47]">{l.titulo}</span>
                  {done && <span className="text-[10px] font-semibold text-[#10B981]">✓ Hecho</span>}
                </div>
                <p className="text-sm text-[#0E2A47]/70 mt-0.5">{l.desc}</p>
                {l.n === 1 && !done && (
                  <div className="mt-2 max-w-xs">
                    <Progress value={(Math.min(docs, 5) / 5) * 100} className="h-1.5" />
                    <div className="text-xs text-[#0E2A47]/50 mt-1">{Math.min(docs, 5)}/5 documentos recomendados</div>
                  </div>
                )}
                {!done && (
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    <Button size="sm" onClick={() => irA(l.n, l.cta.to)} data-testid={`onboarding-nivel-${l.n}-cta`} className="bg-[#0E2A47] hover:bg-[#163C65] h-8 text-xs">
                      {l.cta.label}<ArrowRight size={14} className="ml-1" />
                    </Button>
                    {l.cta2 && (
                      <Button size="sm" variant="outline" onClick={() => irA(l.n, l.cta2.to)} data-testid={`onboarding-nivel-${l.n}-cta2`} className="h-8 text-xs">
                        {l.cta2.label}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-[#E2E8F0] flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-2 text-xs text-[#0E2A47]/60">
          <Smartphone size={14} />
          Llévala en el bolsillo: instala AuditReady como app (menú del navegador → "Añadir a pantalla de inicio").
        </div>
        <Button onClick={completar} data-testid="onboarding-completar"
          className={todo ? "bg-[#10B981] hover:bg-[#0f9d74]" : "bg-[#0E2A47] hover:bg-[#163C65]"}>
          <Check size={15} className="mr-1" />{todo ? "¡Completar primeros pasos!" : "Marcar como completado"}
        </Button>
      </div>
    </Card>
  );
}
